import { ecId, ecLean } from '../ability/context.js';
import { statusRows, statusItemId, STATUS_IDS } from '../statuses/expiry.js';
import { statusScalar } from '../actions/activity.js';
import { oocRoundDue } from './ooc-round-pass.js';
import { calendarInventoryDue } from './calendar-catchup.js';
import { statusInputSnapshot } from './input-snapshots.js';
import type { EndTurnReaders } from './end-turn.js';
import type { CampaignClockCols } from '../../shared/campaign-clock.js';

type Row = Record<string, unknown>;
export const MEMBER_TIME_KIND = 'member_time';
export interface MemberTimeInterval {
    member_id: string;
    from: CampaignClockCols;
    to: CampaignClockCols;
    rounds: number;
    status_fingerprint: string;
}
const record = (v: unknown): Row | null => v !== null && typeof v === 'object' && !Array.isArray(v) ? v as Row : null;

/** A before-value for the member's saved status inputs, never geometry or a restore image. */
export function memberTimeStatusFingerprint(row: Row): string {
    const saved = row._entity_refusal ? record(row._time_source) : null;
    const source = saved ? { encounter_creatures: row.encounter_creatures, statuses: saved.statuses } : row;
    return JSON.stringify(['target', 'caster'].map(mode => statusRows(source, mode as 'target' | 'caster')
        .map(statusInputSnapshot).sort((a, b) => String(a.status_id).localeCompare(String(b.status_id)))));
}

/** Read-through roster selection. No encounter properties or member branches are copied.
 * A fresh proxy target also supports frozen compiler results without violating proxy invariants. */
export function memberTimeScene(encounter: Row, rows: Row[]): Row {
    const get = (key: PropertyKey): unknown => key === 'encounter_creatures_arr' ? rows : Reflect.get(encounter, key);
    return new Proxy({}, {
        get: (_target, key) => get(key),
        has: (_target, key) => key === 'encounter_creatures_arr' || Reflect.has(encounter, key),
        ownKeys: () => [...new Set([...Reflect.ownKeys(encounter), 'encounter_creatures_arr'])],
        getOwnPropertyDescriptor: (_target, key) => key === 'encounter_creatures_arr' || Object.prototype.hasOwnProperty.call(encounter, key)
            ? { configurable: true, enumerable: Object.getOwnPropertyDescriptor(encounter, key)?.enumerable ?? true, writable: false, value: get(key) } : undefined,
        set: () => { throw new TypeError('Member time scene is a read-only held view'); },
    });
}

/** Historical payment depends on the debtor's connected component, not unrelated buffs. */
export function memberTimePeers(members: Row[], member: Row): Row[] {
    const selected = new Set<Row>([member]);
    const ids = new Set([ecId(member)]), connections = new Set<string>();
    let changed = true;
    while (changed) {
        changed = false;
        for (const row of members) {
            const statuses = [...statusRows(row, 'target'), ...statusRows(row, 'caster')];
            if (!selected.has(row) && !ids.has(ecId(row)) && !statuses.some(s => ids.has(String(statusScalar(s, 'status_target_id') ?? ''))
                || ids.has(String(statusScalar(s, 'status_caster_id') ?? ''))
                || connections.has(String(statusScalar(s, 'status_connected_id') ?? '')))) continue;
            if (!selected.has(row)) { selected.add(row); ids.add(ecId(row)); changed = true; }
            for (const s of statuses) {
                for (const key of ['status_target_id', 'status_caster_id']) {
                    const id = String(statusScalar(s, key) ?? '');
                    if (id && !ids.has(id)) { ids.add(id); changed = true; }
                }
                const id = String(statusScalar(s, 'status_connected_id') ?? '');
                if (id && !connections.has(id)) { connections.add(id); changed = true; }
            }
        }
    }
    return [...selected];
}

/** Only independent calendar expiry is replayable without the other members' historical state.
 * A missing compile is never used as a source of computed abilities, vitality or charges. */
export function memberTimeRefusal(encounter: Row, members: Row[], read: EndTurnReaders): string | null {
    if ((read.zoneStatusRows?.() ?? []).some(s => Number(statusScalar(s, 'status_aoe_source') ?? 0))) return 'zones';
    for (const row of members.filter(row => row._entity_refusal)) {
        const saved = record(row._time_source), statuses = record(saved?.statuses);
        if (!Array.isArray(statuses?.target) || !Array.isArray(statuses?.caster)) return 'saved-statuses-unavailable';
        if (!Array.isArray(saved?.inventory)) return 'saved-inventory-unavailable';
    }
    const all = members.filter(row => row._entity_refusal || !Number(ecLean(row, 'encounter_creature_dead') ?? 0)).map(row => {
        const saved = row._entity_refusal ? record(row._time_source) : null;
        return saved ? { encounter_creatures: row.encounter_creatures, statuses: saved.statuses, inventory: saved.inventory } : row;
    });
    const statuses = [...all.flatMap(row => [...statusRows(row, 'target'), ...statusRows(row, 'caster')]), ...(read.zoneStatusRows?.() ?? [])];
    for (const row of all) {
        if (Number(ecLean(row, 'encounter_creature_dead') ?? 0)) return 'death-clock';
        for (const s of [...statusRows(row, 'target'), ...statusRows(row, 'caster')]) {
            if ([STATUS_IDS.dying, STATUS_IDS.drowning, STATUS_IDS.suffocating].includes(statusItemId(s) as never)) return 'death-clock';
            if (statusItemId(s) === STATUS_IDS.summon_tag) return 'summons';
            if (Number(statusScalar(s, 'status_aoe_source') ?? 0)) return 'zones';
            const connected = String(statusScalar(s, 'status_connected_id') ?? '');
            if (connected && statuses.some(peer => String(statusScalar(peer, 'status_connected_id') ?? '') === connected
                && String(statusScalar(peer, 'status_target_id') ?? '') !== ecId(row))) return 'connected-expiry';
            if (String(statusScalar(s, 'status_target_id') ?? ecId(row)) !== ecId(row)
                || String(statusScalar(s, 'status_caster_id') ?? ecId(row)) !== ecId(row)) return 'connected-expiry';
            // Owed activity completion has its own observers and cannot be acknowledged as a plain expiry.
            if ([STATUS_IDS.resting, STATUS_IDS.casting, STATUS_IDS.casting_ritual_spell, STATUS_IDS.gathering,
                STATUS_IDS.lockpicking, STATUS_IDS.reading, STATUS_IDS.training, STATUS_IDS.donning,
                STATUS_IDS.doffing, STATUS_IDS.disenchanting].includes(statusItemId(s) as never)) return 'activity';
        }
        if (oocRoundDue(row, read)) return 'per-round-context';
        if (read.carriesLossTrigger?.(row)) return 'trigger-context';
    }
    if (calendarInventoryDue(encounter, all, { ...read, nowSeconds: Number.POSITIVE_INFINITY })) return 'inventory-clock';
    return null;
}
